/*
 * PFC mount finish gallery — "see it in every finish".
 *
 * Renders the visitor's uploaded photo (or a placeholder) across every mount
 * finish (plain colours + stripe-preset colours + checkers) as a grid of small
 * canvases. Click a tile → opens the mount customiser preset to that finish.
 *
 * Self-contained lightweight renderer — intentionally NOT coupled to the live
 * pfc-mount.js (which also handles personalisation/print/scale/sheet). Shares
 * only the visual conventions (white base, vertical bands, bevelled aperture).
 */
(function () {
  var root = document.querySelector('[data-pfc-mount-gallery]');
  var cfg = window.PFC_MOUNT_GALLERY;
  if (!root || !cfg || !Array.isArray(cfg.tiles) || !cfg.tiles.length) return;

  var UI_FONT = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';
  var STRIPE_BASE = '#fafaf6';

  var elGrid = root.querySelector('[data-grid]');
  var elFile = root.querySelector('[data-file-input]');
  var elUploadBtn = root.querySelector('[data-upload-btn]');
  var elUploadLabel = root.querySelector('[data-upload-label]');
  var elClear = root.querySelector('[data-clear-btn]');
  var elFitToggle = root.querySelector('[data-fit-toggle]');
  var elFitBtns = root.querySelectorAll('[data-fit-mode]');
  var elFitNote = root.querySelector('[data-fit-note]');

  var HANDOFF_KEY = 'pfc-mount-handoff';
  // Sample photos (cycled in the empty state). Back-compat: fall back to the old
  // single placeholderImage if an array wasn't provided.
  var SAMPLES = (Array.isArray(cfg.placeholderImages) && cfg.placeholderImages.length)
    ? cfg.placeholderImages.filter(Boolean)
    : (cfg.placeholderImage ? [cfg.placeholderImage] : []);
  var state = { img: null, usingPlaceholder: true, fit: 'fill', handoffDataUrl: null, sampleIdx: -1 };  // 'fill' = cover-crop, 'fit' = reshape opening
  var tileCanvases = [];   // { canvas, tile }

  /* ---------- Build the grid ---------- */
  function buildTile(tile, parent) {
    var fig = document.createElement('a');
    fig.className = 'pfc-mgal__tile';
    fig.href = cfg.productUrl + '?' + tile.query;
    fig.setAttribute('aria-label', 'Design a ' + tile.label + ' ' + cfg.noun);
    // Carry the visitor's uploaded photo into the customiser (see stashHandoff).
    fig.addEventListener('click', stashHandoff);

    var canvas = document.createElement('canvas');
    canvas.className = 'pfc-mgal__tile-canvas';
    fig.appendChild(canvas);

    var cap = document.createElement('span');
    cap.className = 'pfc-mgal__tile-label';
    cap.textContent = tile.label;
    fig.appendChild(cap);

    var cta = document.createElement('span');
    cta.className = 'pfc-mgal__tile-cta';
    cta.textContent = 'Design this →';
    fig.appendChild(cta);

    parent.appendChild(fig);
    tileCanvases.push({ canvas: canvas, tile: tile });
  }

  function buildGrid() {
    elGrid.innerHTML = '';
    tileCanvases = [];

    // Group tiles by tile.group, preserving first-seen order.
    var order = [], byGroup = {};
    cfg.tiles.forEach(function (tile) {
      var g = tile.group || 'Finishes';
      if (!byGroup[g]) { byGroup[g] = []; order.push(g); }
      byGroup[g].push(tile);
    });

    var grouped = order.length > 1;
    elGrid.classList.toggle('pfc-mgal__groups', grouped);
    elGrid.classList.toggle('pfc-mgal__grid', !grouped);

    order.forEach(function (g) {
      var gridEl;
      if (grouped) {
        var sec = document.createElement('section');
        sec.className = 'pfc-mgal__group';
        var h = document.createElement('h3');
        h.className = 'pfc-mgal__group-title';
        h.textContent = g;
        sec.appendChild(h);
        gridEl = document.createElement('div');
        gridEl.className = 'pfc-mgal__grid';
        sec.appendChild(gridEl);
        // Collapsed-to-one-row expander (shown only if there's a 2nd row).
        var more = document.createElement('button');
        more.type = 'button';
        more.className = 'pfc-mgal__more';
        more.setAttribute('data-grp-toggle', '');
        more.hidden = true;
        more.addEventListener('click', function () {
          sec.dataset.expanded = sec.dataset.expanded === 'true' ? 'false' : 'true';
          clampGroup(sec);
        });
        sec.appendChild(more);
        elGrid.appendChild(sec);
      } else {
        gridEl = elGrid;
      }
      byGroup[g].forEach(function (tile) { buildTile(tile, gridEl); });
    });
  }

  /* Collapse each group's grid to a single row; an expander reveals the rest.
     Clip via max-height + overflow (layout unchanged, so canvases keep their
     width and never need re-rendering). Recomputed on resize. */
  function clampGroups() {
    elGrid.querySelectorAll('.pfc-mgal__group').forEach(clampGroup);
  }
  function clampGroup(sec) {
    var grid = sec.querySelector('.pfc-mgal__grid');
    var btn = sec.querySelector('[data-grp-toggle]');
    if (!grid) return;
    var tiles = grid.querySelectorAll('.pfc-mgal__tile');
    if (tiles.length < 2) { if (btn) btn.hidden = true; grid.style.maxHeight = ''; grid.style.overflow = ''; return; }

    // Row-1 count from current layout (clipping doesn't move tiles).
    var top0 = tiles[0].getBoundingClientRect().top;
    var row1 = 0;
    tiles.forEach(function (t) { if (t.getBoundingClientRect().top <= top0 + 2) row1++; });
    var hidden = tiles.length - row1;
    var expanded = sec.dataset.expanded === 'true';

    if (!expanded && hidden > 0) {
      var gridTop = grid.getBoundingClientRect().top;
      var rowBottom = tiles[0].getBoundingClientRect().bottom - gridTop;
      grid.style.maxHeight = Math.ceil(rowBottom) + 'px';
      grid.style.overflow = 'hidden';
    } else {
      grid.style.maxHeight = '';
      grid.style.overflow = '';
    }

    if (btn) {
      btn.hidden = hidden <= 0;
      btn.textContent = expanded ? 'Show fewer' : ('Show all ' + tiles.length + ' colours →');
      btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }
  }

  /* Stash the uploaded photo so the customiser can pre-load it (sessionStorage
     survives the navigation; the in-memory objectURL does not). Skipped for the
     sample photo. One-shot — the customiser removes it on read. */
  function stashHandoff() {
    try {
      if (state.handoffDataUrl && !state.usingPlaceholder) {
        sessionStorage.setItem(HANDOFF_KEY, state.handoffDataUrl);
      }
    } catch (_) { /* quota / disabled storage — fall back to re-upload */ }
  }

  // Downscaled JPEG dataURL of the uploaded photo for the handoff (caps
  // sessionStorage size; the customiser still lets them Replace at full res).
  function buildHandoff(img) {
    state.handoffDataUrl = null;
    try {
      var MAX = 2200;
      var iw = img.naturalWidth || img.width, ih = img.naturalHeight || img.height;
      if (!iw || !ih) return;
      var s = Math.min(1, MAX / Math.max(iw, ih));
      var w = Math.max(1, Math.round(iw * s)), h = Math.max(1, Math.round(ih * s));
      var cv = document.createElement('canvas'); cv.width = w; cv.height = h;
      cv.getContext('2d').drawImage(img, 0, 0, w, h);
      state.handoffDataUrl = cv.toDataURL('image/jpeg', 0.9);
    } catch (_) { state.handoffDataUrl = null; }
  }

  /* ---------- Tile renderer ---------- */
  function renderTile(canvas, tile) {
    var ctx = canvas.getContext('2d');
    if (!ctx) return;
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var W = Math.max(120, Math.round(canvas.clientWidth || 220));
    var H = Math.max(120, Math.round(canvas.clientHeight || 280));
    var BW = W * dpr, BH = H * dpr;
    if (canvas.width !== BW || canvas.height !== BH) { canvas.width = BW; canvas.height = BH; }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W, H);

    // Backdrop.
    var bg = ctx.createLinearGradient(0, 0, 0, H);
    bg.addColorStop(0, '#f9f4ec'); bg.addColorStop(1, '#f1e9da');
    ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);

    var geom = getGeom();
    var bw = geom.board.w, bh = geom.board.h;
    var MARGIN = 0.82;
    var scale = Math.min(W / bw, H / bh) * MARGIN;
    var outerW = bw * scale, outerH = bh * scale;
    var x0 = (W - outerW) / 2, y0 = (H - outerH) / 2;

    // Board shadow + base fill.
    ctx.save();
    ctx.shadowColor = 'rgba(60,45,25,0.20)';
    ctx.shadowBlur = 16; ctx.shadowOffsetY = 8;
    ctx.fillStyle = (tile.kind === 'plain') ? (tile.hex || '#fafaf6') : STRIPE_BASE;
    ctx.fillRect(x0, y0, outerW, outerH);
    ctx.restore();

    // Pattern.
    if (tile.kind === 'stripes') {
      // Per-tile geometry: fine stripes set bandWidthMm (count derives from the
      // board edge, forced odd so white runs first/last); wide stripes use a
      // fixed band count. orient:'h' runs the bands horizontally.
      var horiz = tile.orient === 'h';
      var n;
      if (tile.bandWidthMm) {
        n = Math.round((horiz ? bh : bw) / tile.bandWidthMm);
        if (n % 2 === 0) n += 1;
        if (n < 5) n = 5;
      } else {
        n = tile.bands || cfg.stripeBands || 19;
      }
      if (horiz) {
        var bandH = outerH / n;
        for (var hi = 0; hi < n; hi++) {
          ctx.fillStyle = (hi % 2 === 0) ? STRIPE_BASE : tile.band;
          ctx.fillRect(x0, y0 + hi * bandH, outerW, Math.ceil(bandH) + 1);
        }
      } else {
        var bandW = outerW / n;
        for (var i = 0; i < n; i++) {
          ctx.fillStyle = (i % 2 === 0) ? STRIPE_BASE : tile.band;
          ctx.fillRect(x0 + i * bandW, y0, Math.ceil(bandW) + 1, outerH);
        }
      }
    } else if (tile.kind === 'checkers') {
      var cell = Math.max(5, (cfg.checkMm || 15) * scale);
      var cols = Math.ceil(outerW / cell), rows = Math.ceil(outerH / cell);
      ctx.save();
      ctx.beginPath(); ctx.rect(x0, y0, outerW, outerH); ctx.clip();
      ctx.fillStyle = tile.band;
      for (var ry = 0; ry < rows; ry++) {
        for (var cx = 0; cx < cols; cx++) {
          if ((cx + ry) % 2 === 0) continue;
          ctx.fillRect(x0 + cx * cell, y0 + ry * cell, Math.ceil(cell) + 1, Math.ceil(cell) + 1);
        }
      }
      ctx.restore();
    } else if (tile.kind === 'gingham') {
      var gcell = Math.max(5, (tile.cellMm || 16) * scale);
      ctx.save();
      ctx.beginPath(); ctx.rect(x0, y0, outerW, outerH); ctx.clip();
      ctx.globalAlpha = 0.42; ctx.fillStyle = tile.band;
      var gcols = Math.ceil(outerW / gcell), grows = Math.ceil(outerH / gcell);
      for (var gx = 0; gx < gcols; gx += 2) ctx.fillRect(x0 + gx * gcell, y0, Math.ceil(gcell) + 1, outerH);
      for (var gy = 0; gy < grows; gy += 2) ctx.fillRect(x0, y0 + gy * gcell, outerW, Math.ceil(gcell) + 1);
      ctx.globalAlpha = 1; ctx.restore();
    } else if (tile.kind === 'polka') {
      var pcell = Math.max(7, (tile.dotMm || 18) * scale), pr = pcell * 0.3;
      ctx.save();
      ctx.beginPath(); ctx.rect(x0, y0, outerW, outerH); ctx.clip();
      ctx.fillStyle = tile.band;
      var prows = Math.ceil(outerH / pcell) + 1, pcols = Math.ceil(outerW / pcell) + 1;
      for (var pry = 0; pry < prows; pry++) {
        for (var pcx = 0; pcx < pcols; pcx++) {
          var ox = x0 + pcx * pcell + (pry % 2 ? pcell / 2 : 0) + pcell / 2;
          ctx.beginPath(); ctx.arc(ox, y0 + pry * pcell + pcell / 2, pr, 0, Math.PI * 2); ctx.fill();
        }
      }
      ctx.restore();
    } else if (tile.kind === 'chevron') {
      var amp = Math.max(4, (tile.bandMm || 14) * scale);
      ctx.save();
      ctx.beginPath(); ctx.rect(x0, y0, outerW, outerH); ctx.clip();
      ctx.strokeStyle = tile.band; ctx.lineWidth = Math.max(2, amp * 0.5); ctx.lineCap = 'butt';
      for (var cyy = y0 - outerH; cyy < y0 + outerH * 2; cyy += amp * 2) {
        ctx.beginPath();
        var up = true;
        for (var cxx = x0; cxx <= x0 + outerW + amp; cxx += amp) {
          var cpy = cyy + (up ? 0 : amp);
          if (cxx === x0) ctx.moveTo(cxx, cpy); else ctx.lineTo(cxx, cpy);
          up = !up;
        }
        ctx.stroke();
      }
      ctx.restore();
    }

    // Surface shade + edge.
    var surf = ctx.createLinearGradient(x0, y0, x0, y0 + outerH);
    surf.addColorStop(0, 'rgba(255,255,255,0.10)'); surf.addColorStop(1, 'rgba(0,0,0,0.06)');
    ctx.fillStyle = surf; ctx.fillRect(x0, y0, outerW, outerH);
    ctx.strokeStyle = tile.border || 'rgba(0,0,0,0.12)';
    ctx.lineWidth = 1; ctx.strokeRect(x0 + 0.5, y0 + 0.5, outerW - 1, outerH - 1);

    // Aperture window.
    var apW = Math.min(geom.opening.w * scale, outerW * 0.92);
    var apH = Math.min(geom.opening.h * scale, outerH * 0.92);
    var apX = x0 + (outerW - apW) / 2, apY = y0 + (outerH - apH) / 2;
    var bevel = Math.max(1.5, Math.min(outerW, outerH) * 0.012);
    var inX = apX + bevel, inY = apY + bevel;
    var inW = Math.max(2, apW - bevel * 2), inH = Math.max(2, apH - bevel * 2);

    // Bevel (simple: white core inset).
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(apX, apY, apW, apH);

    if (state.img) {
      // In Fit mode the opening was cut to the photo's aspect, so cover == no
      // crop. In Fill mode the opening is a standard size and cover centre-crops.
      drawCover(ctx, state.img, inX, inY, inW, inH);
    } else {
      ctx.fillStyle = '#f0ece3'; ctx.fillRect(inX, inY, inW, inH);
    }
    ctx.strokeStyle = 'rgba(0,0,0,0.28)';
    ctx.lineWidth = 1; ctx.strokeRect(inX + 0.5, inY + 0.5, inW - 1, inH - 1);

    // Accent reveal: thin printed keyline on the mount face, offset out from
    // the opening (printable on the single face — not a physical under-mount).
    if (tile.kind === 'accent') {
      var gap = Math.max(3, (tile.gapMm || 10) * scale);
      var lw = Math.max(1, (tile.lineMm || 1.4) * scale);
      var lx = apX - gap, ly = apY - gap, lwd = apW + gap * 2, lht = apH + gap * 2;
      if (lx > x0 + 2 && ly > y0 + 2) {
        ctx.strokeStyle = tile.band; ctx.lineWidth = lw; ctx.strokeRect(lx, ly, lwd, lht);
      }
    }
  }

  function drawCover(ctx, img, dx, dy, dw, dh) {
    var iw = img.naturalWidth || img.width, ih = img.naturalHeight || img.height;
    if (!iw || !ih) return;
    var s = Math.max(dw / iw, dh / ih);
    ctx.save();
    ctx.beginPath(); ctx.rect(dx, dy, dw, dh); ctx.clip();
    ctx.drawImage(img, dx + (dw - iw * s) / 2, dy + (dh - ih * s) / 2, iw * s, ih * s);
    ctx.restore();
  }

  // Geometry per mode. Fill = the configured standard board/opening (photo
  // centre-cropped to it). Fit = cut the opening to the photo's aspect and wrap
  // it in a uniform mount border, so a ready-to-print photo sits in a window of
  // its exact shape — nothing cropped, the whole mount ratio follows the photo.
  function getGeom() {
    if (state.fit === 'fit' && state.img) {
      var iw = state.img.naturalWidth || state.img.width;
      var ih = state.img.naturalHeight || state.img.height;
      if (iw && ih) {
        var ar = iw / ih;
        var LONG = 180;                       // mm — opening long edge
        var BORDER = cfg.fitBorderMm || 50;   // mm — uniform mount border
        var ow = ar >= 1 ? LONG : LONG * ar;
        var oh = ar >= 1 ? LONG / ar : LONG;
        return { board: { w: ow + BORDER * 2, h: oh + BORDER * 2 }, opening: { w: ow, h: oh } };
      }
    }
    return { board: { w: cfg.board.w, h: cfg.board.h }, opening: { w: cfg.opening.w, h: cfg.opening.h } };
  }

  function renderAll() {
    tileCanvases.forEach(function (t) { renderTile(t.canvas, t.tile); });
  }

  /* ---------- Image loading ---------- */
  function loadImage(src, isPlaceholder) {
    var img = new Image();
    img.onload = function () {
      state.img = img;
      state.usingPlaceholder = !!isPlaceholder;
      if (isPlaceholder) state.handoffDataUrl = null; else buildHandoff(img);
      renderAll();
      updateControls();
      // Let the hero (and any other listener) mirror the visitor's photo.
      try { window.dispatchEvent(new CustomEvent('pfc-mount-sample', { detail: { src: src, isPlaceholder: !!isPlaceholder } })); } catch (_) {}
    };
    img.onerror = function () { state.img = null; renderAll(); };
    if (!isPlaceholder) img.crossOrigin = 'anonymous';
    img.src = src;
  }

  // Sample photo: random on first paint, then cycle to the next on repeat clicks.
  function loadSample(mode) {
    if (!SAMPLES.length) return;
    if (mode === 'next') state.sampleIdx = (state.sampleIdx + 1) % SAMPLES.length;
    else state.sampleIdx = SAMPLES.length > 1 ? Math.floor(Math.random() * SAMPLES.length) : 0;
    loadImage(SAMPLES[state.sampleIdx], true);
  }

  function noteFor(mode) {
    return mode === 'fit'
      ? 'Fit: the opening is cut to your photo’s shape — nothing cropped. Pick your outer size when you design.'
      : 'Fill: your photo is cropped to a standard opening. Fine-tune the crop when you design.';
  }

  function updateControls() {
    if (elUploadLabel) elUploadLabel.textContent = state.usingPlaceholder ? 'Upload your photo' : 'Change photo';
    // Sample button always visible; marked active when the sample is showing
    // (clicking then just reloads it — harmless).
    if (elClear) {
      if (SAMPLES.length) {
        // Samples exist → always offer them. While a sample shows, cycle to the
        // next ("Try another sample"); after an upload, return to a sample.
        elClear.hidden = false;
        elClear.textContent = (state.usingPlaceholder && SAMPLES.length > 1) ? 'Try another sample' : 'Use sample photo';
        elClear.classList.toggle('is-active', state.usingPlaceholder);
        elClear.setAttribute('aria-pressed', state.usingPlaceholder ? 'true' : 'false');
      } else {
        // No sample → offer "Remove photo" once the visitor has uploaded one.
        elClear.hidden = state.usingPlaceholder || !state.img;
        elClear.textContent = 'Remove photo';
        elClear.classList.remove('is-active');
        elClear.removeAttribute('aria-pressed');
      }
    }
    // Fit toggle + crop note only make sense once a photo is shown.
    var hasImg = !!state.img;
    if (elFitToggle) elFitToggle.hidden = !hasImg;
    if (elFitNote) { elFitNote.textContent = noteFor(state.fit); elFitNote.hidden = !hasImg; }
  }

  function setFit(mode) {
    state.fit = (mode === 'fit') ? 'fit' : 'fill';
    if (elFitBtns) elFitBtns.forEach(function (b) {
      var on = b.getAttribute('data-fit-mode') === state.fit;
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    if (elFitNote) elFitNote.textContent = noteFor(state.fit);
    renderAll();
  }

  /* ---------- Wiring ---------- */
  if (elUploadBtn) elUploadBtn.addEventListener('click', function () { elFile && elFile.click(); });
  if (elFile) elFile.addEventListener('change', function () {
    var f = elFile.files && elFile.files[0];
    if (!f) return;
    var url = URL.createObjectURL(f);
    loadImage(url, false);
    elFile.value = '';
  });
  if (elClear) elClear.addEventListener('click', function () {
    if (SAMPLES.length) loadSample('next');
    else { state.img = null; state.usingPlaceholder = true; renderAll(); updateControls(); }
  });
  if (elFitBtns) elFitBtns.forEach(function (b) {
    b.addEventListener('click', function () { setFit(b.getAttribute('data-fit-mode')); });
  });

  var resizeRaf = null;
  window.addEventListener('resize', function () {
    if (resizeRaf) cancelAnimationFrame(resizeRaf);
    resizeRaf = requestAnimationFrame(function () { renderAll(); clampGroups(); });
  });
  window.addEventListener('load', clampGroups);   // re-measure after fonts/CSS settle

  /* ---------- Init ---------- */
  buildGrid();
  renderAll();              // empty paper first paint (all tiles visible)
  clampGroups();            // then collapse each group to one row
  updateControls();
  loadSample('random');     // random sample on load (no-op if none configured)
})();
